import React, { useState, useEffect, useCallback } from 'react';
import { apiService, graphBuilderApi } from '../../../services/api';
import './CacheManagementPage.css';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatTTL(seconds) {
  if (seconds == null || seconds < 0) return 'No expiry';
  if (seconds === 0) return 'Expired';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

function ttlClass(seconds) {
  if (seconds == null || seconds < 0) return 'ttl-badge--ok';
  if (seconds === 0) return 'ttl-badge--expired';
  if (seconds < 600) return 'ttl-badge--warn';
  return 'ttl-badge--ok';
}

/* ── Confirmation Modal ─────────────────────────────────────── */

function ConfirmModal({ title, message, onConfirm, onCancel, busy }) {
  return (
    <div className="cache-confirm-overlay" onClick={onCancel}>
      <div className="cache-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="cache-confirm-actions">
          <button className="btn-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn-confirm-delete" onClick={onConfirm} disabled={busy}>
            {busy ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Collapsible Cache Section ──────────────────────────────── */

function CacheSection({ title, count, defaultOpen, actions, children }) {
  const [open, setOpen] = useState(defaultOpen ?? true);

  return (
    <div className="cache-section">
      <div className="cache-section-header" onClick={() => setOpen((o) => !o)}>
        <h3>
          <span className={`section-chevron ${open ? 'section-chevron--open' : ''}`}>&#9654;</span>
          {title}
          <span className="section-count">{count}</span>
        </h3>
        {actions && (
          <div className="section-actions" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
      {open && children}
    </div>
  );
}

/* ── Main Page ──────────────────────────────────────────────── */

function CacheManagementPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Redis data
  const [sessions, setSessions] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [idempotency, setIdempotency] = useState([]);
  const [totals, setTotals] = useState({});

  // Graph data
  const [graphs, setGraphs] = useState([]);
  const [graphDbStats, setGraphDbStats] = useState({});

  // Pool stats (from GraphBuilder)
  const [poolStats, setPoolStats] = useState(null);

  // Selection state
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [selectedPrompts, setSelectedPrompts] = useState(new Set());
  const [selectedIdempotency, setSelectedIdempotency] = useState(new Set());
  const [selectedGraphs, setSelectedGraphs] = useState(new Set());

  // Modal state
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const clearSuccess = useCallback(() => {
    setTimeout(() => setSuccess(null), 4000);
  }, []);

  /* ── Data fetching ─────────────────────────────────────────── */

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [redisSummary, graphSummary, poolSummary] = await Promise.allSettled([
        apiService.getCacheSummary(),
        graphBuilderApi.listCachedGraphs(),
        apiService.getGraphPoolStats(),
      ]);

      if (redisSummary.status === 'fulfilled') {
        const r = redisSummary.value;
        setSessions(r.sessions || []);
        setPrompts(r.prompts || []);
        setIdempotency(r.idempotency || []);
        setTotals(r.totals || {});
      } else {
        setSessions([]);
        setPrompts([]);
        setIdempotency([]);
        setTotals({});
      }

      if (graphSummary.status === 'fulfilled') {
        const g = graphSummary.value;
        setGraphs(g.graphs || []);
        setGraphDbStats(g.db_cache_stats || {});
      } else {
        setGraphs([]);
        setGraphDbStats({});
      }

      if (poolSummary.status === 'fulfilled') {
        setPoolStats(poolSummary.value);
      } else {
        setPoolStats(null);
      }

      if (redisSummary.status === 'rejected' && graphSummary.status === 'rejected') {
        setError('Failed to load cache data from both Redis and GraphBuilder.');
      }
    } catch (exc) {
      setError(exc?.message || 'Failed to load cache data');
    } finally {
      setLoading(false);
      setSelectedSessions(new Set());
      setSelectedPrompts(new Set());
      setSelectedIdempotency(new Set());
      setSelectedGraphs(new Set());
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── Toggle helpers ────────────────────────────────────────── */

  function toggleSet(set, setter, key) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAllInSet(items, keyFn, selected, setter) {
    if (selected.size === items.length) {
      setter(new Set());
    } else {
      setter(new Set(items.map(keyFn)));
    }
  }

  /* ── Delete handlers ───────────────────────────────────────── */

  async function handleDeleteSessions(keys, all) {
    setDeleting(true);
    try {
      const res = await apiService.deleteRedisSessions(keys, all);
      setSuccess(`Deleted ${res.deleted} session(s)`);
      clearSuccess();
      await fetchData();
    } catch (exc) {
      setError(exc?.message || 'Failed to delete sessions');
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  }

  async function handleDeletePrompts(keys, all) {
    setDeleting(true);
    try {
      const res = await apiService.deleteRedisPrompts(keys, all);
      setSuccess(`Deleted ${res.deleted} prompt cache key(s)`);
      clearSuccess();
      await fetchData();
    } catch (exc) {
      setError(exc?.message || 'Failed to delete prompt cache');
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  }

  async function handleDeleteIdempotency(keys, all) {
    setDeleting(true);
    try {
      const res = await apiService.deleteRedisIdempotency(keys, all);
      setSuccess(`Deleted ${res.deleted} idempotency key(s)`);
      clearSuccess();
      await fetchData();
    } catch (exc) {
      setError(exc?.message || 'Failed to delete idempotency cache');
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  }

  async function handleDeleteGraphs(graphIds, all) {
    setDeleting(true);
    try {
      if (all) {
        const res = await graphBuilderApi.deleteAllGraphCaches();
        setSuccess(
          `Cleared all graph caches: ${res.compile_deleted} files, ${res.pools_unloaded} pools`
        );
      } else {
        let totalCleared = 0;
        for (const gid of graphIds) {
          await graphBuilderApi.deleteGraphCache(gid);
          totalCleared++;
        }
        setSuccess(`Cleared cache for ${totalCleared} graph(s)`);
      }
      clearSuccess();
      await fetchData();
    } catch (exc) {
      setError(exc?.message || 'Failed to delete graph cache');
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  }

  async function handleEvictPool(graphId) {
    setDeleting(true);
    try {
      await apiService.evictGraphPool(graphId);
      setSuccess(`Evicted pool for graph ${graphId.substring(0, 8)}...`);
      clearSuccess();
      await fetchData();
    } catch (exc) {
      setError(exc?.message || 'Failed to evict graph pool');
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  }

  async function handleReloadPool(graphId) {
    setDeleting(true);
    try {
      await apiService.reloadGraphPool(graphId);
      setSuccess(`Reloaded pool for graph ${graphId.substring(0, 8)}...`);
      clearSuccess();
      await fetchData();
    } catch (exc) {
      setError(exc?.message || 'Failed to reload graph pool');
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  }

  async function handleDeleteEverything() {
    setDeleting(true);
    try {
      const [redisRes, graphRes] = await Promise.allSettled([
        apiService.deleteAllRedis(),
        graphBuilderApi.deleteAllGraphCaches(),
      ]);
      const parts = [];
      if (redisRes.status === 'fulfilled') parts.push(`${redisRes.value.deleted} Redis keys`);
      if (graphRes.status === 'fulfilled') parts.push('all graph caches');
      setSuccess(`Deleted ${parts.join(' and ') || 'caches'}`);
      clearSuccess();
      await fetchData();
    } catch (exc) {
      setError(exc?.message || 'Failed to delete all caches');
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  }

  /* ── Render ────────────────────────────────────────────────── */

  const totalRedis = (totals.total || 0);
  const totalGraphs = graphs.length;

  return (
    <div className="user-home-layout__page cache-mgmt-page">
      <div className="user-home-layout__page-header">
        <h1 className="user-home-layout__page-title">Cache Management</h1>
        <p className="user-home-layout__page-subtitle">
          Inspect and clear cached data across Redis and GraphBuilder
        </p>
      </div>

      {/* Summary stats */}
      {!loading && (
        <div className="cache-stats-bar">
          <span className="stat-chip">
            Redis Keys: <span className="stat-value">{totalRedis}</span>
          </span>
          <span className="stat-chip">
            Sessions: <span className="stat-value">{totals.sessions || 0}</span>
          </span>
          <span className="stat-chip">
            Prompt Keys: <span className="stat-value">{totals.prompts || 0}</span>
          </span>
          <span className="stat-chip">
            Idempotency: <span className="stat-value">{totals.idempotency || 0}</span>
          </span>
          <span className="stat-chip">
            Cached Graphs: <span className="stat-value">{totalGraphs}</span>
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div className="cache-toolbar">
        <div className="cache-toolbar-left">
          <button className="btn-refresh" onClick={fetchData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        <button
          className="btn-danger"
          disabled={loading || (totalRedis === 0 && totalGraphs === 0)}
          onClick={() =>
            setConfirm({
              title: 'Delete All Caches',
              message:
                'This will delete ALL Redis cache entries (sessions, prompts, idempotency) and ALL GraphBuilder compiled graph caches. This action cannot be undone.',
              onConfirm: handleDeleteEverything,
            })
          }
        >
          Delete All Caches
        </button>
      </div>

      {error && <div className="cache-error">{error}</div>}
      {success && <div className="cache-success">{success}</div>}

      {loading && <div className="cache-loading">Loading cache data...</div>}

      {!loading && (
        <div className="user-home-layout__card">
          <h2 className="user-home-layout__section-title">Redis Cache</h2>

          {/* ── Sessions ──────────────────────────────────────── */}
          <CacheSection
            title="Sessions"
            count={sessions.length}
            actions={
              <>
                <button
                  className="btn-danger btn-danger-sm"
                  disabled={selectedSessions.size === 0}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete Selected Sessions',
                      message: `Delete ${selectedSessions.size} selected session(s)?`,
                      onConfirm: () =>
                        handleDeleteSessions(Array.from(selectedSessions), false),
                    })
                  }
                >
                  Delete Selected ({selectedSessions.size})
                </button>
                <button
                  className="btn-danger btn-danger-sm"
                  disabled={sessions.length === 0}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete All Sessions',
                      message: `Delete all ${sessions.length} session(s) from Redis? Active users will be logged out.`,
                      onConfirm: () => handleDeleteSessions([], true),
                    })
                  }
                >
                  Delete All
                </button>
              </>
            }
          >
            {sessions.length === 0 ? (
              <div className="cache-empty">No active sessions in Redis</div>
            ) : (
              <div className="cache-table-wrap">
                <table className="cache-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedSessions.size === sessions.length && sessions.length > 0}
                          onChange={() =>
                            toggleAllInSet(sessions, (s) => s.key, selectedSessions, setSelectedSessions)
                          }
                        />
                      </th>
                      <th>Session ID</th>
                      <th>User</th>
                      <th>Company</th>
                      <th>TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s) => (
                      <tr key={s.key}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedSessions.has(s.key)}
                            onChange={() => toggleSet(selectedSessions, setSelectedSessions, s.key)}
                          />
                        </td>
                        <td className="cell-key" title={s.session_id}>{s.session_id}</td>
                        <td className="cell-detail">{s.email || s.username || s.user_id || '-'}</td>
                        <td className="cell-detail">{s.company_id || '-'}</td>
                        <td>
                          <span className={`ttl-badge ${ttlClass(s.ttl_seconds)}`}>
                            {formatTTL(s.ttl_seconds)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CacheSection>

          {/* ── Prompt Templates ──────────────────────────────── */}
          <CacheSection
            title="Prompt Templates"
            count={prompts.length}
            actions={
              <>
                <button
                  className="btn-danger btn-danger-sm"
                  disabled={selectedPrompts.size === 0}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete Selected Prompt Keys',
                      message: `Delete ${selectedPrompts.size} selected prompt cache key(s)?`,
                      onConfirm: () =>
                        handleDeletePrompts(Array.from(selectedPrompts), false),
                    })
                  }
                >
                  Delete Selected ({selectedPrompts.size})
                </button>
                <button
                  className="btn-danger btn-danger-sm"
                  disabled={prompts.length === 0}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete All Prompt Cache',
                      message: `Delete all ${prompts.length} prompt cache key(s)?`,
                      onConfirm: () => handleDeletePrompts([], true),
                    })
                  }
                >
                  Delete All
                </button>
              </>
            }
          >
            {prompts.length === 0 ? (
              <div className="cache-empty">No prompt templates cached in Redis</div>
            ) : (
              <div className="cache-table-wrap">
                <table className="cache-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedPrompts.size === prompts.length && prompts.length > 0}
                          onChange={() =>
                            toggleAllInSet(prompts, (p) => p.key, selectedPrompts, setSelectedPrompts)
                          }
                        />
                      </th>
                      <th>Slug</th>
                      <th>Preview</th>
                      <th>TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prompts.map((p) => (
                      <tr key={p.key}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPrompts.has(p.key)}
                            onChange={() => toggleSet(selectedPrompts, setSelectedPrompts, p.key)}
                          />
                        </td>
                        <td className="cell-detail">{p.slug}</td>
                        <td className="cell-key" title={p.value_preview}>{p.value_preview || '-'}</td>
                        <td>
                          <span className={`ttl-badge ${ttlClass(p.ttl_seconds)}`}>
                            {formatTTL(p.ttl_seconds)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CacheSection>

          {/* ── Idempotency ───────────────────────────────────── */}
          <CacheSection
            title="Idempotency Keys"
            count={idempotency.length}
            defaultOpen={false}
            actions={
              <>
                <button
                  className="btn-danger btn-danger-sm"
                  disabled={selectedIdempotency.size === 0}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete Selected Idempotency Keys',
                      message: `Delete ${selectedIdempotency.size} selected key(s)?`,
                      onConfirm: () =>
                        handleDeleteIdempotency(Array.from(selectedIdempotency), false),
                    })
                  }
                >
                  Delete Selected ({selectedIdempotency.size})
                </button>
                <button
                  className="btn-danger btn-danger-sm"
                  disabled={idempotency.length === 0}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete All Idempotency Keys',
                      message: `Delete all ${idempotency.length} idempotency key(s)?`,
                      onConfirm: () => handleDeleteIdempotency([], true),
                    })
                  }
                >
                  Delete All
                </button>
              </>
            }
          >
            {idempotency.length === 0 ? (
              <div className="cache-empty">No idempotency keys in Redis</div>
            ) : (
              <div className="cache-table-wrap">
                <table className="cache-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            selectedIdempotency.size === idempotency.length &&
                            idempotency.length > 0
                          }
                          onChange={() =>
                            toggleAllInSet(
                              idempotency,
                              (i) => i.key,
                              selectedIdempotency,
                              setSelectedIdempotency
                            )
                          }
                        />
                      </th>
                      <th>Key</th>
                      <th>Status</th>
                      <th>TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {idempotency.map((item) => (
                      <tr key={item.key}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIdempotency.has(item.key)}
                            onChange={() =>
                              toggleSet(selectedIdempotency, setSelectedIdempotency, item.key)
                            }
                          />
                        </td>
                        <td className="cell-key" title={item.idempotency_key}>
                          {item.idempotency_key}
                        </td>
                        <td className="cell-detail">{item.status || '-'}</td>
                        <td>
                          <span className={`ttl-badge ${ttlClass(item.ttl_seconds)}`}>
                            {formatTTL(item.ttl_seconds)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CacheSection>
        </div>
      )}

      {/* ── Graph Cache ──────────────────────────────────────── */}
      {!loading && (
        <div className="user-home-layout__card" style={{ marginTop: 24 }}>
          <h2 className="user-home-layout__section-title">Graph Cache</h2>
          <p className="user-home-layout__card-text">
            Compiled graph artifacts, engine pools, and DB subgraph caches from GraphBuilder.
            Graph caches are universal and shared across all users and executions.
          </p>

          <CacheSection
            title="Cached Graphs"
            count={graphs.length}
            actions={
              <>
                <button
                  className="btn-danger btn-danger-sm"
                  disabled={selectedGraphs.size === 0}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete Selected Graph Caches',
                      message: `Clear cache for ${selectedGraphs.size} selected graph(s)?`,
                      onConfirm: () =>
                        handleDeleteGraphs(Array.from(selectedGraphs), false),
                    })
                  }
                >
                  Delete Selected ({selectedGraphs.size})
                </button>
                <button
                  className="btn-danger btn-danger-sm"
                  disabled={graphs.length === 0}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete All Graph Caches',
                      message:
                        'This will remove all compiled artifacts, unload all engine pools, and clear all DB caches. Next query will trigger recompilation.',
                      onConfirm: () => handleDeleteGraphs([], true),
                    })
                  }
                >
                  Delete All
                </button>
              </>
            }
          >
            {graphs.length === 0 ? (
              <div className="cache-empty">No cached graphs found</div>
            ) : (
              <div className="cache-table-wrap">
                <table className="cache-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedGraphs.size === graphs.length && graphs.length > 0}
                          onChange={() =>
                            toggleAllInSet(
                              graphs,
                              (g) => g.graph_id,
                              selectedGraphs,
                              setSelectedGraphs
                            )
                          }
                        />
                      </th>
                      <th>Graph ID</th>
                      <th>Layers</th>
                      <th>Nodes</th>
                      <th>Connections</th>
                      <th>Disk Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {graphs.map((g) => (
                      <tr key={g.graph_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedGraphs.has(g.graph_id)}
                            onChange={() =>
                              toggleSet(selectedGraphs, setSelectedGraphs, g.graph_id)
                            }
                          />
                        </td>
                        <td className="cell-key" title={g.graph_id}>
                          {g.graph_id.substring(0, 8)}...
                        </td>
                        <td>
                          {(g.layers || []).map((l) => (
                            <span key={l} className={`layer-badge layer-badge--${l}`}>
                              {l}
                            </span>
                          ))}
                        </td>
                        <td className="cell-detail">{g.node_count || 0}</td>
                        <td className="cell-detail">{g.synapse_count || 0}</td>
                        <td className="cell-size">{formatBytes(g.disk_bytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CacheSection>
        </div>
      )}

      {/* ── SNN Graph Pools ────────────────────────────────── */}
      {!loading && poolStats && (
        <div className="user-home-layout__card" style={{ marginTop: 24 }}>
          <h2 className="user-home-layout__section-title">Engine Graph Pools</h2>
          <p className="user-home-layout__card-text">
            Live engine instances loaded in GraphBuilder memory.
            Each pool holds multiple copies of a compiled graph for concurrent execution.
          </p>

          <div className="cache-stats-bar">
            <span className="stat-chip">
              Loaded Pools: <span className="stat-value">{poolStats.total_pools}</span>
            </span>
            <span className="stat-chip">
              Total Networks: <span className="stat-value">{poolStats.total_networks}</span>
            </span>
            <span className="stat-chip">
              Pool Memory: <span className="stat-value">{poolStats.estimated_total_memory_mb?.toFixed(1)} MB</span>
            </span>
            <span className="stat-chip">
              Process RSS: <span className="stat-value">{poolStats.process_rss_mb?.toFixed(0)} MB</span>
              {' / '}
              <span className="stat-value">{poolStats.memory_limit_mb} MB</span>
            </span>
            <span className="stat-chip">
              Evictions: <span className="stat-value">{poolStats.eviction_count}</span>
            </span>
            <span className="stat-chip">
              Max Graphs: <span className="stat-value">{poolStats.max_loaded_graphs === 0 ? 'Unlimited' : poolStats.max_loaded_graphs}</span>
            </span>
            <span className="stat-chip">
              Pool Size: <span className="stat-value">{poolStats.pool_size_config}</span>
            </span>
          </div>

          <CacheSection
            title="Active Pools"
            count={(poolStats.pools || []).length}
          >
            {(poolStats.pools || []).length === 0 ? (
              <div className="cache-empty">No graphs currently loaded in memory</div>
            ) : (
              <div className="cache-table-wrap">
                <table className="cache-table">
                  <thead>
                    <tr>
                      <th>Graph ID</th>
                      <th>Pool Size</th>
                      <th>Available</th>
                      <th>Active</th>
                      <th>Nodes</th>
                      <th>Connections</th>
                      <th>Memory</th>
                      <th>Load Time</th>
                      <th>Last Used</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(poolStats.pools || []).map((p) => (
                      <tr key={p.graph_id}>
                        <td className="cell-key" title={p.graph_id}>
                          {p.graph_id.substring(0, 8)}...
                        </td>
                        <td className="cell-detail">{p.pool_size}</td>
                        <td className="cell-detail">{p.available}</td>
                        <td className="cell-detail">
                          <span className={p.active_checkouts > 0 ? 'ttl-badge ttl-badge--warn' : ''}>
                            {p.active_checkouts}
                          </span>
                        </td>
                        <td className="cell-detail">{p.neurons}</td>
                        <td className="cell-detail">{p.synapses}</td>
                        <td className="cell-size">{p.estimated_memory_mb?.toFixed(1)} MB</td>
                        <td className="cell-detail">{(p.load_time_ms / 1000).toFixed(1)}s</td>
                        <td className="cell-detail">
                          {p.last_used_at
                            ? new Date(p.last_used_at * 1000).toLocaleTimeString()
                            : '-'}
                        </td>
                        <td>
                          <button
                            className="btn-danger btn-danger-sm"
                            style={{ marginRight: 4 }}
                            disabled={deleting}
                            onClick={() =>
                              setConfirm({
                                title: 'Evict Graph Pool',
                                message: `Evict pool for graph ${p.graph_id.substring(0, 8)}...? Active checkouts: ${p.active_checkouts}`,
                                onConfirm: () => handleEvictPool(p.graph_id),
                              })
                            }
                          >
                            Evict
                          </button>
                          <button
                            className="btn-refresh"
                            style={{ fontSize: 12, padding: '2px 8px' }}
                            disabled={deleting}
                            onClick={() => handleReloadPool(p.graph_id)}
                          >
                            Reload
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CacheSection>
        </div>
      )}

      {/* ── Confirmation modal ───────────────────────────────── */}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
          busy={deleting}
        />
      )}
    </div>
  );
}

export default CacheManagementPage;
